import {
  Input,
  Select,
  SelectType,
} from "@appquality/appquality-design-system";
import { JournalText } from "react-bootstrap-icons";
import { CampaignAdditionalField } from "src/services/tryberApi";
import { MetadataGrid } from "../styled";
import ShowItem from "./ShowItem";
import { BugData, BugEditValues } from "./types";

interface Props {
  data: BugData;
  isEditing: boolean;
  editedValues: BugEditValues;
  additionalFields?: CampaignAdditionalField[];
  onChange: (fields: NonNullable<BugEditValues["campaign_fields"]>) => void;
}

/**
 * Helper function to find the field definition from campaign additionalFields
 * Matches by field_name (from bug) to name (from campaign)
 */
const getFieldDefinition = (
  fieldName: string,
  additionalFields?: CampaignAdditionalField[],
): CampaignAdditionalField | undefined => {
  if (!additionalFields) return undefined;
  return additionalFields.find((field) => field.name === fieldName);
};

const BugCampaignFields = ({
  data,
  isEditing,
  editedValues,
  additionalFields,
  onChange,
}: Props) => {
  if (!data.campaign_fields || data.campaign_fields.length === 0) return null;

  const currentFields = editedValues.campaign_fields || data.campaign_fields;

  return (
    <MetadataGrid>
      {data.campaign_fields.map((field) => {
        const currentValue =
          currentFields.find((f) => f.field_id === field.field_id)?.value ??
          field.value ??
          "";

        const fieldDefinition = getFieldDefinition(
          field.field_name,
          additionalFields,
        );
        const isSelectField = fieldDefinition?.type === "select";

        return (
          <ShowItem
            key={field.field_id}
            icon={JournalText}
            label={field.field_name}
          >
            {isEditing ? (
              isSelectField && fieldDefinition.type === "select" ? (
                <Select
                  name={`campaign-field-${field.field_id}`}
                  label=""
                  options={fieldDefinition.options.map((opt) => ({
                    value: opt,
                    label: opt,
                  }))}
                  value={
                    currentValue
                      ? ({
                          value: currentValue,
                          label: currentValue,
                        } as SelectType.Option)
                      : ({ value: "", label: "—" } as SelectType.Option)
                  }
                  onChange={(option: SelectType.Option | null) => {
                    const updated = currentFields.map((f) =>
                      f.field_id === field.field_id
                        ? { ...f, value: option?.value?.toString() || "" }
                        : f,
                    );
                    onChange(updated);
                  }}
                  isSearchable={false}
                />
              ) : (
                <Input
                  id={`campaign-field-${field.field_id}`}
                  type="text"
                  value={currentValue}
                  onChange={(val: string) => {
                    const updated = currentFields.map((f) =>
                      f.field_id === field.field_id ? { ...f, value: val } : f,
                    );
                    onChange(updated);
                  }}
                />
              )
            ) : (
              field.value || "—"
            )}
          </ShowItem>
        );
      })}
    </MetadataGrid>
  );
};

export default BugCampaignFields;
